import { api } from '@/global/services/api'
import L from 'leaflet'

const ICON_SIZE = {
  iconSize: [30, 30],
  iconAnchor: [15, 33]
}

export default {
  name: 'IndexTableLeafletMapHelper',

  data () {
    return {
      leafletMap: null,
      showLoader: null,
      oldSelectedItem: null,
      queryParams: {},
      statusIcons: {
        green: L.divIcon({
          ...ICON_SIZE,
          className: 'green_icon',
          html: '<span class="custom-marker green_icon" />'
        }),
        blue: L.divIcon({
          ...ICON_SIZE,
          className: 'blue_icon',
          html: '<span class="custom-marker blue_icon" />'
        }),
        indigo: L.divIcon({
          ...ICON_SIZE,
          className: 'indigo_icon',
          html: '<span class="custom-marker indigo_icon" />'
        }),
        orange: L.divIcon({
          ...ICON_SIZE,
          className: 'orange_icon',
          html: '<span class="custom-marker orange_icon" />'
        }),
        dark_orange: L.divIcon({
          ...ICON_SIZE,
          className: 'dark_orange_icon',
          html: '<span class="custom-marker dark_orange_icon" />'
        }),
        purple: L.divIcon({
          ...ICON_SIZE,
          className: 'purple_icon',
          html: '<span class="custom-marker purple_icon" />'
        }),
        dark_purple: L.divIcon({
          ...ICON_SIZE,
          className: 'dark_purple_icon',
          html: '<span class="custom-marker dark_purple_icon" />'
        }),
        teal: L.divIcon({
          ...ICON_SIZE,
          className: 'teal_icon',
          html: '<span class="custom-marker teal_icon" />'
        }),
        dark_teal: L.divIcon({
          ...ICON_SIZE,
          className: 'dark_teal_icon',
          html: '<span class="custom-marker dark_teal_icon" />'
        }),
        cyan: L.divIcon({
          ...ICON_SIZE,
          className: 'cyan_icon',
          html: '<span class="custom-marker cyan_icon" />'
        }),
        dark_cyan: L.divIcon({
          ...ICON_SIZE,
          className: 'dark_cyan_icon',
          html: '<span class="custom-marker dark_cyan_icon" />'
        }),
        brown: L.divIcon({
          ...ICON_SIZE,
          className: 'brown_icon',
          html: '<span class="custom-marker brown_icon" />'
        }),
        dark_brown: L.divIcon({
          ...ICON_SIZE,
          className: 'dark_brown_icon',
          html: '<span class="custom-marker dark_brown_icon" />'
        }),
        lime: L.divIcon({
          ...ICON_SIZE,
          className: 'lime_icon',
          html: '<span class="custom-marker lime_icon" />'
        }),
        dark_lime: L.divIcon({
          ...ICON_SIZE,
          className: 'dark_lime_icon',
          html: '<span class="custom-marker dark_lime_icon" />'
        }),
        grey: L.divIcon({
          ...ICON_SIZE,
          className: 'grey_icon',
          html: '<span class="custom-marker grey_icon" />'
        }),
        white: L.divIcon({
          ...ICON_SIZE,
          className: 'white_icon',
          html: '<span class="custom-marker white_icon" />'
        })
      }
    }
  },

  methods: {
    async mapInitialized (event) {
      const { map, queryParams } = event || {}

      this.queryParams = queryParams || {}
      if (map) {
        this.leafletMap = map
        await this.fetchMarkers()
      }
    },

    async fetchMarkers () {
      try {
        if (this.leafletMap) {
          this.showLoader = true
          const { data } = await api('road-maintenance').get('traffic-regulation/icons', this.queryParams)

          if (data && data.length) {
            const markersConfig = {
              markers: data,
              icons: this.statusIcons,
              markerClusterOptions: {
                maxClusterRadius: 40
              },
              fitMarkers: true,
              fitBoundsOptions: {
                pad: 0.5
              }
            }

            markersConfig.markers.forEach(marker => {
              marker.popup = {
                popupOptions: {
                  autoPan: false,
                  offset: [-5, -10]
                },
                popupData: async () => await this.getMarkersTooltipData(marker)
              }

              marker.click = () => this.onMarkerClick(marker)
            })
            this.$nextTick(() => {
              this.leafletMap?.onMapReady(function (map) {
                map.generateMarkers(markersConfig)
              })
            })
          }
          else {
            this.showLoader = false
          }
        }
      }
      catch (exception) {
        console.error(exception)
        this.showLoader = false
      }
    },

    // Zoom on marker when user dbl click on table row
    showMarker (event) {
      try {
        const { id: markerId } = event?.data || {}
        if (markerId && this.leafletMap) {
          // Each marker on map has an id defined so that we can find them by it
          const newSelectedMarker = this.leafletMap.findMarkerById(markerId)

          // ************************* Old selected marker styling *************************

          // If there is already selected marker, reset its icon size to default [12, 12] and remove 'selected' class from it
          if (this.oldSelectedItem) {
            // Find old selected marker object
            const oldSelectedMarker = this.leafletMap.findMarkerById(this.oldSelectedItem.id)

            if (oldSelectedMarker) {
              // Get class from old selected marker so that we can remove previously added 'selected' class from it by applying new empty class ''
              const oldSelectedMarkerClass = oldSelectedMarker.getIcon()?.options?.className

              if (oldSelectedMarkerClass) {
                // Define new icon
                const newIcon = L.divIcon({
                  className: oldSelectedMarkerClass,
                  html: '<span class="custom-marker ' + oldSelectedMarkerClass + '" />'
                })
                // Apply new styled icon to old selected marker
                oldSelectedMarker.setIcon(newIcon)
              }
            }
          }

          // ************************* New selected marker styling *************************
          if (newSelectedMarker) {
            const { lat, lng } = newSelectedMarker?._latlng || {}

            if (lat && lng) {
              this.leafletMap.$refs?.map?.mapObject?.setView(L.latLng([lat, lng]), 14)
              // Set styling for new selected marker (css class and icon size)
              const newSelectedMarkerClass = newSelectedMarker.getIcon()?.options?.className

              if (newSelectedMarkerClass) {
                // Define new icon
                const newIcon = L.divIcon({
                  className: newSelectedMarkerClass,
                  html: '<span class="custom-marker ' + newSelectedMarkerClass + ' selected" />'
                })
                // Apply new icon to new selected marker
                newSelectedMarker.setIcon(newIcon)
              }

              // Remember new selected item as old so that we can find marker object later when new selected marker comes
              this.oldSelectedItem = event.data
            }
          }
        }
      }
      catch (exception) {
        console.error(exception)
      }
    },

    resetIconOnMapDragEnd () {
      const { id: currentSelectedMarkerId } = this.oldSelectedItem || {}

      if (currentSelectedMarkerId) {
        // Find old selected marker object
        const oldSelectedMarker = this.leafletMap.findMarkerById(currentSelectedMarkerId)

        if (oldSelectedMarker) {
          // Get class from old selected marker so that we can remove previously added 'selected' class from it by applying new empty class ''
          const oldSelectedMarkerClass = oldSelectedMarker.getIcon()?.options?.className

          if (oldSelectedMarkerClass) {
            // Define new icon
            const newIcon = L.divIcon({
              className: oldSelectedMarkerClass,
              html: '<span class="custom-marker ' + oldSelectedMarkerClass + '" />'
            })
            // Apply new styled icon to old selected marker
            oldSelectedMarker.setIcon(newIcon)
          }
        }
      }
    },

    async getMarkersTooltipData (marker) {
      const { validity, address } = marker || {}

      return '<div> ' +
        '<span><b>' + this.$t('road-maintenance/patrol-service.address') + ':</b> ' + (address || '') + '</span>' +
        '<br>' +
        '<span><b>' + this.$t('road-maintenance.date') + ':</b> ' + (validity || '') + '</span>' +
        '</div>'
    }
  }
}
